import { useState } from 'react';
import api from '../lib/api';

interface Metric {
  id: string;
  name: string;
  description: string;
  unit?: string;
  currentValue?: number;
  targetValue?: number;
}

interface UpdateMetricModalProps {
  metric: Metric | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UpdateMetricModal({
  metric,
  isOpen,
  onClose,
  onSuccess,
}: UpdateMetricModalProps) {
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !metric) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/api/metrics/${metric.id}/data`, {
        value: numValue,
        source: 'manual',
      });

      setValue('');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update metric');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValue('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-cyber-bg-primary/80 backdrop-blur-sm flex items-center justify-center z-50 animate-slide-in">
      <div className="cyber-card border-2 border-cyber-neon-blue shadow-neon-blue max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-display font-bold text-cyber-neon-blue uppercase tracking-wider">
                &gt; UPDATE METRIC
              </h2>
              <p className="text-sm text-cyber-text-secondary mt-1 font-mono">{metric.name}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-cyber-text-muted hover:text-cyber-neon-pink text-2xl transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-cyber-error/10 border border-cyber-error rounded-lg p-3">
                <p className="text-sm text-cyber-error font-mono">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="value" className="block text-sm font-mono font-medium text-cyber-text-primary mb-2 uppercase tracking-wider">
                &gt; New Value {metric.unit && `(${metric.unit})`}
              </label>
              <input
                type="number"
                id="value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                step="any"
                required
                className="cyber-input font-mono text-xl"
                placeholder={`${metric.currentValue || 0}`}
              />
            </div>

            {metric.currentValue !== null && metric.currentValue !== undefined && (
              <div className="cyber-card bg-cyber-bg-tertiary border border-cyber-border">
                <p className="text-xs text-cyber-text-muted font-mono mb-1 uppercase">Current Value</p>
                <p className="text-2xl font-bold font-mono text-cyber-neon-blue">
                  {metric.currentValue} {metric.unit}
                </p>
              </div>
            )}

            {metric.targetValue && (
              <div className="cyber-card bg-cyber-neon-green/10 border border-cyber-neon-green">
                <p className="text-xs text-cyber-neon-green font-mono mb-1 uppercase">Target Value</p>
                <p className="text-2xl font-bold font-mono text-cyber-neon-green">
                  {metric.targetValue} {metric.unit}
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 font-display text-sm font-medium text-cyber-text-primary bg-cyber-bg-tertiary border border-cyber-border rounded-lg hover:border-cyber-text-muted transition-all uppercase tracking-wider"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 cyber-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <div className="cyber-spinner mr-2 w-4 h-4"></div>
                    UPDATING...
                  </span>
                ) : (
                  'UPDATE'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
